<script>
    import { onMount } from "svelte";
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
    import { workCursorTrailItems } from "$lib/data/cursorTrailItems";
    import { soundcloudTracks } from "$lib/data/soundcloudTracks";

    // Matches SoundCloud's own embed generator format — only the colon is encoded,
    // not the slashes, which their player requires.
    const scEmbedUrl = (id) =>
        `https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${id}&color=%2300b0ff&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
    const scPlaylistEmbedUrl =
        "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/soundcloud%253Aplaylists%253A2205868073&color=%2300b0ff&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true";

    const portfolioFilters = [
        { id: "websites", label: "Websites" },
        { id: "software", label: "Software" },
        { id: "animation", label: "Animation" },
        { id: "graphic-design", label: "Design" },
        { id: "audio", label: "Audio" },
    ];
    const defaultPortfolioFilter = "websites";

    let portfolioEl;
    let filterEl;
    let filterTrackEl;
    let trackDropClip = "inset(0 0 0 0 round 999px)";
    let activePortfolioFilter = defaultPortfolioFilter;
    let activePortfolioFilterIndex = 0;
    let portfolioFilterDirection = "positive";
    let portfolioFilterInitiated = false;
    let hasFilteredItems = true;

    const normalizeCategory = (value) =>
        value.toLowerCase().trim().replace(/\s+/g, "-");

    const getPortfolioFilterIndex = (filterId) =>
        portfolioFilters.findIndex((filter) => filter.id === filterId);

    const resolvePortfolioFilter = (value) => {
        const normalized = normalizeCategory(value ?? "");
        return getPortfolioFilterIndex(normalized) >= 0
            ? normalized
            : defaultPortfolioFilter;
    };

    const getPortfolioFilterFromUrl = () => {
        if (typeof window === "undefined") return defaultPortfolioFilter;

        const typeParam = new URL(window.location.href).searchParams.get(
            "type",
        );
        return resolvePortfolioFilter(typeParam);
    };

    const updatePortfolioFilterUrl = (filterId, replace = false) => {
        if (typeof window === "undefined") return;

        const url = new URL(window.location.href);
        url.searchParams.set("type", filterId);
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

        if (nextUrl === currentUrl) return;

        if (replace) {
            window.history.replaceState(window.history.state, "", nextUrl);
            return;
        }

        window.history.pushState(window.history.state, "", nextUrl);
    };

    const getItemCategories = (anchor) => {
        const rawCategories = anchor.dataset.category ?? "websites";

        return rawCategories
            .split(",")
            .map((token) => normalizeCategory(token))
            .filter(Boolean);
    };

    const ensurePortfolioCategories = () => {
        if (!portfolioEl) return;

        const portfolioItems = Array.from(portfolioEl.querySelectorAll("a"));
        portfolioItems.forEach((anchor) => {
            if (!anchor.dataset.category) {
                anchor.dataset.category = defaultPortfolioFilter;
            }
        });
    };

    const applyPortfolioFilter = () => {
        if (!portfolioEl) return;

        // Audio tracks are rendered in a separate Svelte section, not as <a> elements.
        if (activePortfolioFilter === "audio") {
            const portfolioItems = Array.from(
                portfolioEl.querySelectorAll("a"),
            );
            portfolioItems.forEach((anchor) => {
                anchor.hidden = true;
                anchor.style.display = "none";
                anchor.setAttribute("aria-hidden", "true");
            });
            portfolioEl.hidden = true;
            portfolioEl.style.display = "none";
            hasFilteredItems = soundcloudTracks.length > 0;
            return;
        }

        portfolioEl.hidden = false;
        portfolioEl.style.display = "";

        const portfolioItems = Array.from(portfolioEl.querySelectorAll("a"));
        let visibleCount = 0;

        portfolioItems.forEach((anchor) => {
            const isMatch = getItemCategories(anchor).includes(
                activePortfolioFilter,
            );

            // Use both hidden + inline display for robustness against global CSS/script interference.
            anchor.hidden = !isMatch;
            anchor.style.display = isMatch ? "" : "none";
            anchor.setAttribute("aria-hidden", isMatch ? "false" : "true");
            if (isMatch) visibleCount += 1;
        });

        hasFilteredItems = visibleCount > 0;
    };

    const updateMagneticDrop = () => {
        if (!filterEl || !filterTrackEl) return;

        const tabs = Array.from(filterTrackEl.querySelectorAll("button"));
        const activeTab = tabs[activePortfolioFilterIndex];
        if (!activeTab) return;

        const trackRect = filterTrackEl.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        const offsetPx = Math.max(0, tabRect.left - trackRect.left);
        const trailingPx = Math.max(
            0,
            trackRect.width - (offsetPx + tabRect.width),
        );

        trackDropClip = `inset(0 ${trailingPx.toFixed(3)}px 0 ${offsetPx.toFixed(3)}px round 999px)`;
    };

    const selectPortfolioFilter = (
        filterId,
        index,
        { syncUrl = true, replaceUrl = false } = {},
    ) => {
        if (
            filterId === activePortfolioFilter &&
            index === activePortfolioFilterIndex
        ) {
            return;
        }

        portfolioFilterDirection =
            index >= activePortfolioFilterIndex ? "positive" : "negative";
        activePortfolioFilter = filterId;
        activePortfolioFilterIndex = index;

        applyPortfolioFilter();
        requestAnimationFrame(updateMagneticDrop);

        if (syncUrl) {
            updatePortfolioFilterUrl(filterId, replaceUrl);
        }
    };

    const handleFilterKeydown = (event) => {
        const isHorizontalNav =
            event.key === "ArrowRight" || event.key === "ArrowLeft";
        const isBoundaryNav = event.key === "Home" || event.key === "End";
        if (!isHorizontalNav && !isBoundaryNav) return;

        event.preventDefault();

        let nextIndex = activePortfolioFilterIndex;

        if (event.key === "ArrowRight") {
            nextIndex =
                (activePortfolioFilterIndex + 1) % portfolioFilters.length;
        } else if (event.key === "ArrowLeft") {
            nextIndex =
                (activePortfolioFilterIndex - 1 + portfolioFilters.length) %
                portfolioFilters.length;
        } else if (event.key === "Home") {
            nextIndex = 0;
        } else if (event.key === "End") {
            nextIndex = portfolioFilters.length - 1;
        }

        const nextFilter = portfolioFilters[nextIndex];
        selectPortfolioFilter(nextFilter.id, nextIndex);
        requestAnimationFrame(() => {
            const tabs = filterTrackEl?.querySelectorAll("button");
            tabs?.[nextIndex]?.focus();
        });
    };

    onMount(() => {
        let lightbox;
        let destroyed = false;
        let galleryEl;
        let onGalleryClick;
        const handleResize = () => updateMagneticDrop();
        const handlePopState = () => {
            const nextFilterId = getPortfolioFilterFromUrl();
            const nextFilterIndex = getPortfolioFilterIndex(nextFilterId);
            selectPortfolioFilter(nextFilterId, nextFilterIndex, {
                syncUrl: false,
            });
        };

        const initialFilterId = getPortfolioFilterFromUrl();
        const initialFilterIndex = getPortfolioFilterIndex(initialFilterId);
        activePortfolioFilter = initialFilterId;
        activePortfolioFilterIndex =
            initialFilterIndex >= 0 ? initialFilterIndex : 0;

        ensurePortfolioCategories();
        applyPortfolioFilter();
        requestAnimationFrame(() => {
            updateMagneticDrop();
            portfolioFilterInitiated = true;
        });
        window.addEventListener("resize", handleResize);
        window.addEventListener("popstate", handlePopState);
        updatePortfolioFilterUrl(activePortfolioFilter, true);

        (async () => {
            try {
                const { default: PhotoSwipeLightbox } =
                    await import("photoswipe/lightbox");

                if (destroyed) return;

                galleryEl = portfolioEl;
                if (!galleryEl) return;

                lightbox = new PhotoSwipeLightbox({
                    bgOpacity: 0.9,
                    gallery: galleryEl,
                    children: "a:not([hidden])",
                    secondaryZoomLevel: "fill",
                    pswpModule: () => import("photoswipe"),
                });

                // Keep thumbnail/alt extraction stable when anchors wrap <picture>.
                lightbox.addFilter("domItemData", (itemData, element) => {
                    const img = element.querySelector("img");
                    if (img) {
                        itemData.msrc = img.currentSrc || img.src;
                        if (!itemData.alt) {
                            itemData.alt = img.alt;
                        }
                    }
                    return itemData;
                });

                lightbox.init();
                window.__workGalleryReady = true;

                // On secondary zoom, animate pan to top of image so tall
                // screenshots can be read top-to-bottom like the real app.
                lightbox.on("openingAnimationEnd", () => {
                    const pswp = lightbox.pswp;
                    if (!pswp || pswp.__zoomTopPatched) return;
                    pswp.__zoomTopPatched = true;

                    const origZoomTo = pswp.zoomTo.bind(pswp);
                    pswp.zoomTo = (
                        destZoomLevel,
                        centerPoint,
                        transitionDuration,
                        ignoreBounds,
                    ) => {
                        const slide = pswp.currSlide;
                        const isSecondary =
                            slide &&
                            Math.abs(
                                destZoomLevel - slide.zoomLevels.secondary,
                            ) < 0.001;

                        origZoomTo(
                            destZoomLevel,
                            centerPoint,
                            transitionDuration,
                            ignoreBounds,
                        );

                        if (!isSecondary) return;

                        // After zoom animation ends, smooth-scroll to top of image.
                        setTimeout(
                            () => {
                                const s = pswp.currSlide;
                                if (!s) return;
                                const startY = s.pan.y;
                                const targetY = s.bounds.max.y;
                                if (Math.abs(startY - targetY) < 1) return;

                                const dur = 300;
                                const t0 = performance.now();
                                const tick = (now) => {
                                    const p = Math.min((now - t0) / dur, 1);
                                    const ease = 1 - (1 - p) ** 3; // ease-out cubic
                                    s.pan.y =
                                        startY + (targetY - startY) * ease;
                                    s.applyCurrentZoomPan();
                                    if (p < 1) requestAnimationFrame(tick);
                                };
                                requestAnimationFrame(tick);
                            },
                            (transitionDuration ?? 333) + 20,
                        );
                    };
                });

                // Fallback: force interception even if delegated click binding is missed.
                onGalleryClick = (event) => {
                    if (
                        event.defaultPrevented ||
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                    ) {
                        return;
                    }

                    const anchor = event.target?.closest(
                        "#portfolio a:not([hidden])",
                    );
                    if (!anchor || !galleryEl) return;

                    const anchors = Array.from(
                        galleryEl.querySelectorAll("a:not([hidden])"),
                    );
                    const index = anchors.indexOf(anchor);
                    if (index < 0) return;

                    event.preventDefault();
                    lightbox.loadAndOpen(
                        index,
                        { gallery: galleryEl },
                        { x: event.clientX, y: event.clientY },
                    );
                };

                galleryEl.addEventListener("click", onGalleryClick);

                try {
                    const { default: PhotoSwipeDynamicCaption } =
                        await import("photoswipe-dynamic-caption-plugin");
                    if (!destroyed) {
                        new PhotoSwipeDynamicCaption(lightbox, {
                            type: "auto",
                        });
                    }
                } catch (captionError) {
                    console.warn(
                        "PhotoSwipe caption plugin failed to load; continuing without captions.",
                        captionError,
                    );
                }
            } catch (error) {
                window.__workGalleryReady = false;
                console.error("Failed to initialize PhotoSwipe gallery", error);
            }
        })();

        return () => {
            destroyed = true;
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("popstate", handlePopState);
            if (galleryEl && onGalleryClick) {
                galleryEl.removeEventListener("click", onGalleryClick);
            }
            lightbox?.destroy();
        };
    });
</script>

<svelte:head>
    <title>dinglandia | Design & Development Portfolio</title>
    <meta
        name="description"
        content="View our designs showcasing different industry landscapes, get in touch to create your vision on the web."
    />

    <meta property="og:title" content="Design & Development Portfolio" />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/work" />
    <meta
        property="og:description"
        content="View our designs showcasing different industry landscapes, get in touch to create your vision on the web."
    />
    <meta property="og:type" content="business.business" />
    <meta property="og:image" content="https://dinglandia.design/og/work.jpg" />

    <link href="/styles/photoswipe-esm.css" rel="stylesheet" />
    <link
        href="/styles/photoswipe-dynamic-caption-plugin.css"
        rel="stylesheet"
    />
</svelte:head>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new" name="logo"></div>
    <h1 class="flash">work</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail items={workCursorTrailItems} />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#work" aria-label="Jump to work gallery">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Work -->
<section id="work" class="work">
    <div class="container">
        <h2 class="text-center">Design & Development Portfolio</h2>
        <div class="portfolio-filter-wrap">
            <div
                class="portfolio-filter"
                bind:this={filterEl}
                data-direction={portfolioFilterDirection}
                data-initiated={portfolioFilterInitiated}
            >
                <div
                    class="track trackdrop"
                    aria-hidden="true"
                    style:clip-path={trackDropClip}
                >
                    {#each portfolioFilters as filter}
                        <span>{filter.label}</span>
                    {/each}
                </div>

                <div
                    class="track trackbase"
                    bind:this={filterTrackEl}
                    role="group"
                    aria-label="Portfolio categories"
                >
                    {#each portfolioFilters as filter, index}
                        <button
                            type="button"
                            class:active={activePortfolioFilter === filter.id}
                            aria-pressed={activePortfolioFilter === filter.id}
                            on:click={() =>
                                selectPortfolioFilter(filter.id, index)}
                            on:keydown={handleFilterKeydown}
                        >
                            {filter.label}
                        </button>
                    {/each}
                </div>
            </div>
        </div>

        <!-- Portfolio -->
        <div id="portfolio" bind:this={portfolioEl}>
            <a
                href="/img/portfolio/dinglandia-lab-rebrand.avif"
                data-pswp-width="1920"
                data-pswp-height="17287"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/dinglandia-lab-rebrand.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/dinglandia-lab-rebrand.webp"
                            alt="labonlineagency.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">LAB Online Agency</h2>
                        <p>labonlineagency.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/dinglandia.design--gilbertson-fine-homes.avif"
                data-pswp-width="1920"
                data-pswp-height="5681"
            >
                <figure>
                    <img
                        src="/img/portfolio/dinglandia.design--gilbertson-fine-homes.avif"
                        alt="gilbertsonfinehomes.com"
                    />
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Gilbertson Fine Homes</h2>
                        <p>gilbertsonfinehomes.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/dinglandia.design--labonlineagency-landing.avif"
                data-pswp-width="1920"
                data-pswp-height="8617"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/dinglandia.design--labonlineagency-landing.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/dinglandia.design--labonlineagency-landing.webp"
                            alt="labonlineagency.ca"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">
                            Lab Online Agency - Landing Page
                        </h2>
                        <p>labonlineagency.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/dinglandia.design--clayton-dog-walkers.avif"
                data-pswp-width="1920"
                data-pswp-height="2376"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/dinglandia.design--clayton-dog-walkers.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/dinglandia.design--clayton-dog-walkers.webp"
                            alt="claytondogwalkers.ca"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Clayton Dog Walkers</h2>
                        <p>claytondogwalkers.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                class="link"
                href="/img/portfolio/realtree-thumb.avif"
                data-pswp-width="1920"
                data-pswp-height="9926"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/realtree-thumb.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/realtree-thumb.webp"
                            alt="realtree.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Realtree Camo Apparel</h2>
                        <p>realtree.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/the-dawg-cafe-thumb.avif"
                data-pswp-width="1920"
                data-pswp-height="3817"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/the-dawg-cafe-thumb.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/the-dawg-cafe-thumb.webp"
                            alt="thedawgcafe.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">The Dawg Cafe</h2>
                        <p>thedawgcafe.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/uptown-dawg-full.avif"
                data-pswp-width="1920"
                data-pswp-height="7854"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/uptown-dawg.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/uptown-dawg.webp"
                            alt="uptowndawg.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Uptown Dawg</h2>
                        <p>uptowndawg.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/antons-pasta-full.avif"
                data-pswp-width="1920"
                data-pswp-height="1609"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/antons-pasta.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/antons-pasta.webp"
                            alt="antonspastabar.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Antons Pasta Bar</h2>
                        <p>antonspastabar.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/artisan-construction-full.avif"
                data-pswp-width="1920"
                data-pswp-height="2439"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/artisan-construction.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/artisan-construction.webp"
                            data-original-src="/img/portfolio/artisan-construction-full.webp"
                            alt="artisanconstruction.ca"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Artisan Construction</h2>
                        <p>artisanconstruction.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/send-to-news-full.avif"
                data-pswp-width="1920"
                data-pswp-height="4790"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/send-to-news.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/send-to-news.webp"
                            alt="stnvideo.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Send to News Video</h2>
                        <p>stnvideo.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/ixcela-full.avif"
                data-pswp-width="1920"
                data-pswp-height="3911"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/ixcela.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/ixcela.webp"
                            alt="ixcela.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Ixcela Health</h2>
                        <p>ixcela.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/yluim-full.avif"
                data-pswp-width="1920"
                data-pswp-height="5298"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/yluim.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/yluim.webp"
                            alt="YLUIM designs"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Yluim Designs</h2>
                        <p>yluim.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/versa-homes-full.avif"
                data-pswp-width="1920"
                data-pswp-height="2601"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/versa-homes.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/versa-homes.webp"
                            alt="versahomes.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Versa Homes</h2>
                        <p>versahomes.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/rail-and-river-full.avif"
                data-pswp-width="1920"
                data-pswp-height="3254"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/rail-and-river.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/rail-and-river.webp"
                            alt="railandriverbistro.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Rail & River Bistro</h2>
                        <p>railandriverbistro.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/worklete-blog-full.avif"
                data-pswp-width="1920"
                data-pswp-height="2377"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/worklete-blog.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/worklete-blog.webp"
                            alt="Worklete Blog"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Worklete Blog</h2>
                        <p>worklete.com/blog</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/closers-full.avif"
                data-pswp-width="1920"
                data-pswp-height="4573"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/closers.avif"
                            type="image/avif"
                        />
                        <img src="/img/portfolio/closers.webp" alt="Closers" />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Closers</h2>
                        <p>closers.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/matt-magee-realty-full.avif"
                data-pswp-width="1920"
                data-pswp-height="2531"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/matt-magee-realty.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/matt-magee-realty.webp"
                            alt="mattmagee.ca"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Matt Magee Realty</h2>
                        <p>mattmagee.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/naturallygiven-full.avif"
                data-pswp-width="1920"
                data-pswp-height="3768"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/naturallygiven.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/naturallygiven.webp"
                            alt="Naturally Given"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Naturally Given Remedies</h2>
                        <p>natureallygiven.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/madlab-gym-full.avif"
                data-pswp-width="1920"
                data-pswp-height="3716"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/madlab-gym.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/madlab-gym.webp"
                            alt="madlab.ca"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Madlab CrossFit</h2>
                        <p>madlab.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/hunter-helicopters-full.avif"
                data-pswp-width="1920"
                data-pswp-height="1743"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/hunter-helicopters.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/hunter-helicopters.webp"
                            alt="hunterhelicopters.com"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Hunter Helicopters</h2>
                        <p>hunterhelicopters.com</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/apple-security-full.avif"
                data-pswp-width="1920"
                data-pswp-height="4721"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/apple-security.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/apple-security.webp"
                            alt="Apple Security"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Apple Security</h2>
                        <p>applesecurity.ca</p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
            <a
                href="/img/portfolio/software/dinglandia-software--gmail-to-notion-sync.avif"
                data-pswp-width="1920"
                data-pswp-height="6288"
                data-category="software"
            >
                <figure>
                    <picture>
                        <source
                            srcset="/img/portfolio/software/dinglandia-software--gmail-to-notion-sync.avif"
                            type="image/avif"
                        />
                        <img
                            src="/img/portfolio/software/dinglandia-software--gmail-to-notion-sync.webp"
                            alt="Gmail to Notion Sync"
                        />
                    </picture>
                    <figcaption class="pswp-caption-content">
                        <h2 class="style skew">Gmail to Notion Sync</h2>
                        <p>
                            Automatically filters emails into Notion databases
                            for DEV &amp; TOOLS
                        </p>
                        <ul class="list-inline">
                            <li class="list-inline-item">
                                <div class="icon-new" name="html"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="css"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="js"></div>
                            </li>
                            <li class="list-inline-item">
                                <div class="icon-new" name="notion"></div>
                            </li>
                        </ul>
                    </figcaption>
                </figure>
            </a>
        </div>
        <!-- //Portfolio -->

        {#if activePortfolioFilter === "audio"}
            <div id="audio-portfolio" aria-label="Audio tracks">
                <article class="audio-track audio-track--featured">
                    <iframe
                        title="Warrior of the Gods playlist"
                        width="100%"
                        height="300"
                        scrolling="no"
                        frameborder="no"
                        allow="autoplay"
                        loading="lazy"
                        src={scPlaylistEmbedUrl}
                    ></iframe>
                </article>
                {#each soundcloudTracks as track (track.id)}
                    <article class="audio-track">
                        <iframe
                            title={track.title}
                            width="100%"
                            height="300"
                            scrolling="no"
                            frameborder="no"
                            allow="autoplay"
                            loading="lazy"
                            src={scEmbedUrl(track.id)}
                        ></iframe>
                    </article>
                {/each}
            </div>
        {/if}

        <p class="portfolio-empty-state" hidden={hasFilteredItems}>
            No {portfolioFilters[activePortfolioFilterIndex]?.label} projects yet.
        </p>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a
                        href="/contact"
                        role="button"
                        class="btn btn-lg btn-custom"
                        data-fillout-id="n2vCxyr9Huus"
                        data-fillout-embed-type="popup"
                        data-fillout-dynamic-resize
                        data-fillout-inherit-parameters
                        data-fillout-popup-size="small">start your project</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Work -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img
                src="/img/mountains/mtnBlue.svg"
                alt=""
                style="--offset:0.012"
            />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- .//New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>
        <div class="services-grid">
            <a href="/services#v-pills-discover">
                <svg class="icon icon-discover"
                    ><use xlink:href="#icon-discover"></use></svg
                >
                <p>Discover</p>
            </a>
            <a href="/services#v-pills-design">
                <svg class="icon icon-design"
                    ><use xlink:href="#icon-design"></use></svg
                >
                <p>Design</p>
            </a>
            <a href="/services#v-pills-develop">
                <svg class="icon icon-develop"
                    ><use xlink:href="#icon-develop"></use></svg
                >
                <p>Develop</p>
            </a>
            <a href="/services#v-pills-devops">
                <svg class="icon icon-devops"
                    ><use xlink:href="#icon-devops"></use></svg
                >
                <p>DevOps</p>
            </a>
            <a href="/services#v-pills-agentic">
                <svg class="icon icon-robot"
                    ><use xlink:href="#icon-robot"></use></svg
                >
                <p>Agentic</p>
            </a>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a
                        href="/contact"
                        class="btn btn-lg btn-custom"
                        type="button">get in touch</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <!-- <div class="scroll-indicator">↓ scroll to sail ↓</div> -->

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>

<!-- Water Ripple CTA -->

<style>
    .portfolio-filter-wrap {
        margin: 1.5rem auto 0;
        max-width: 100%;
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .portfolio-filter-wrap::-webkit-scrollbar {
        display: none;
    }

    .portfolio-filter {
        --accent: hsl(202 100% 56%);
        position: relative;
        width: max-content;
        margin-inline: auto;
        opacity: 0;
        transition: opacity 0.55s ease;
    }

    .portfolio-filter[data-initiated="true"] {
        opacity: 1;
    }

    .portfolio-filter .track {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.28rem;
        border-radius: 999px;
    }

    .portfolio-filter .trackdrop {
        position: absolute;
        inset: 0;
        z-index: 2;
        pointer-events: none;
        color: var(--color-white, var(--color-white-hex));
        border-radius: 999px;
        background:
            linear-gradient(hsl(0 0% 100% / 0.25), transparent 50%),
            linear-gradient(var(--accent), hsl(202 92% 46%));
        clip-path: inset(0 80% 0 0 round 999px);
        opacity: 0;
    }

    .portfolio-filter .trackdrop::before {
        content: "";
        position: absolute;
        inset: 1px;
        border-radius: inherit;
        background: linear-gradient(
            hsl(0 0% 100% / 0.55),
            hsl(0 0% 100% / 0.05) 60%,
            transparent
        );
        opacity: 0.6;
    }

    .portfolio-filter .trackdrop span,
    .portfolio-filter .trackbase button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: "Lato", system-ui, sans-serif;
        font-size: 0.96rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        padding: 0.45rem 1rem 0.42rem;
        border-radius: 999px;
        line-height: 1;
        white-space: nowrap;
    }

    .portfolio-filter .trackbase {
        position: relative;
        z-index: 1;
    }

    .portfolio-filter .trackbase button {
        appearance: none;
        -webkit-appearance: none;
        border: 0;
        background: transparent;
        color: light-dark(
            var(--color-black, var(--color-black-hex)),
            var(--color-white, var(--color-white-hex))
        );
        cursor: pointer;
        opacity: 0.6;
        transition: opacity 0.2s ease;
    }

    .portfolio-filter .trackbase button:hover,
    .portfolio-filter .trackbase button:focus-visible,
    .portfolio-filter .trackbase button.active {
        opacity: 1;
    }

    .portfolio-filter[data-initiated="true"] .trackdrop,
    .portfolio-filter[data-initiated="true"] .trackdrop::before {
        opacity: 1;
        transition:
            opacity 0.2s ease,
            clip-path 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .portfolio-filter[data-direction="positive"][data-initiated="true"]
        .trackdrop,
    .portfolio-filter[data-direction="positive"][data-initiated="true"]
        .trackdrop::before {
        transition-delay: 0s, 0.045s;
    }

    .portfolio-filter[data-direction="negative"][data-initiated="true"]
        .trackdrop,
    .portfolio-filter[data-direction="negative"][data-initiated="true"]
        .trackdrop::before {
        transition-delay: 0s, 0s;
    }

    .portfolio-empty-state {
        margin-top: -1.5rem;
        margin-bottom: 2rem;
        text-align: center;
        font-family: "Lato", system-ui, sans-serif;
        font-size: 1rem;
        color: light-dark(
            var(--color-black, var(--color-black-hex)),
            var(--color-white, var(--color-white-hex))
        );
        opacity: 0.75;
    }

    @media (max-width: 620px) {
        .portfolio-filter {
            margin-inline: 0;
        }

        .portfolio-filter .trackdrop span,
        .portfolio-filter .trackbase button {
            font-size: 0.875rem;
            padding-inline: 0.82rem;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .portfolio-filter,
        .portfolio-filter .trackbase button,
        .portfolio-filter .trackdrop,
        .portfolio-filter .trackdrop::before {
            transition: none !important;
        }
    }

    #audio-portfolio {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 480px), 1fr));
        gap: 0.75rem;
        margin-block: 4rem 2rem;
    }

    .audio-track iframe {
        display: block;
        border-radius: 8px;
        overflow: hidden;
    }

    .audio-track--featured {
        grid-column: 1 / -1;
    }
</style>
